import { useCallback, useMemo, useState } from "react";

import { deserializeAnswer, serializeAnswer } from "~/utils/quizzes";

import { Menu } from "../menu/Menu";
import { MenuItem } from "../menu/MenuItem";
import { DropdownMenu } from "../dropdownMenu/DropdownMenu";
import { Text } from "../text/Text";

import "./FillInTheBlankQuestion.css";

import type { DropdownMenuStateAndHelpers } from "../dropdownMenu/DropdownMenu";
import type { QuizQuestionChangeEvent, QuizQuestionOption } from "../../app";

const DEFAULT_PLACEHOLDER = "Select";

export interface FillInTheBlankQuestionAnswerElement {
  part: string;
  value: string;
}

export interface FillInTheBlankQuestionProps {
  answer: string[];
  options: QuizQuestionOption[];
  template: string;
  isValid?: boolean;
  placeholder?: string;
  showValidity?: boolean;
  value?: string[];
  onChange?: (event: QuizQuestionChangeEvent) => void;
}

export function getIntent({
  answer,
  part,
  value,
  showValidity,
}: {
  answer: string[];
  part: string;
  value: string[];
  showValidity?: boolean;
}) {
  if (!showValidity) return;

  const userAnswer = deserializeAnswer(value).find((v) => v.key === part);

  if (!userAnswer) return "error";

  const correctAnswer = answer
    .find((a) => a.split(":")[0] === part)
    ?.split(":")[1];

  if (userAnswer) {
    return correctAnswer === userAnswer?.value ? "success" : "error";
  }
}

export function FillInTheBlankQuestion({
  answer,
  isValid,
  options,
  placeholder = DEFAULT_PLACEHOLDER,
  showValidity,
  template,
  value = [],
  onChange,
}: FillInTheBlankQuestionProps) {
  const parsedValue = useMemo(() => deserializeAnswer(value), [value]);
  const [currentPart, setCurrentPart] = useState("");

  const renderOptions = useCallback(
    ({ close }: DropdownMenuStateAndHelpers) => {
      return (
        <Menu>
          {options.map((option) => {
            const isAlreadySelected = Boolean(
              parsedValue.find((v) => v.value === option.value),
            );

            function handleMenuItemClick() {
              if (!parsedValue) return;

              const answer = { key: currentPart, value: option.value };

              // Ensure only one entry per "part" token is
              // saved
              const newValue = parsedValue
                .filter((v) => v.key !== answer.key)
                .concat(answer);

              // Emit the change event value in the
              // "part:value" format
              onChange?.({
                value: serializeAnswer(newValue),
              });

              setCurrentPart("");
              close();
            }

            return (
              <MenuItem
                key={option.value}
                value={option.value}
                onClick={handleMenuItemClick}
              >
                {isAlreadySelected ? (
                  <Text color="gray">
                    <s>{option.text}</s>
                  </Text>
                ) : (
                  option.text
                )}
              </MenuItem>
            );
          })}
        </Menu>
      );
    },
    [currentPart, onChange, options, parsedValue],
  );

  const renderQuestion = useCallback(() => {
    const regex = /(\$[a-z0-9]+)/;

    // Replace "$1" tokens with their corresponding options
    return template.split(regex).map((part) => {
      if (part.match(regex)) {
        const intent = getIntent({
          value,
          answer,
          part,
          showValidity,
        });

        const label = options.find(
          (o) => o.value === parsedValue.find((v) => v.key === part)?.value,
        )?.text;

        const handleMenuOpen = () => {
          setCurrentPart(part);
        };

        return options ? (
          <DropdownMenu
            key={part}
            disabled={isValid}
            intent={intent}
            label={label}
            placeholder={placeholder}
            style={{ width: "auto" }}
            size="sm"
            onOpen={handleMenuOpen}
          >
            {renderOptions}
          </DropdownMenu>
        ) : (
          part
        );
      }

      return part;
    });
  }, [
    answer,
    isValid,
    options,
    parsedValue,
    placeholder,
    renderOptions,
    showValidity,
    template,
    value,
  ]);

  return <div className="FillInTheBlankQuestion">{renderQuestion()}</div>;
}
